import { compare } from 'compare-versions'
import { pageVisibleOnce } from './page-visible-internal'
import type { AuthServiceMethods } from './services/auth'
import type { UserServiceMethods } from './services/user'
import type { PermissionServiceMethods } from './services/permission'
import type { GeneralServiceMethods } from './services/general'
import type { IAPServiceMethods } from './services/iap'
import type { AdditionalServiceMethods } from './services/additional'
import type { L10nServiceMethods } from './services/l10n'
import type { DataServiceMethods } from './services/data'

export interface NativeServices {
  auth: AuthServiceMethods
  user: UserServiceMethods
  permission: PermissionServiceMethods
  general: GeneralServiceMethods
  iap: IAPServiceMethods
  data: DataServiceMethods
  // only for relax-paywall app
  additional: AdditionalServiceMethods
  l10n: {
    getLocalizedString: (payload: {
      key: string
      params?: { type: 'integer' | 'float' | 'string'; value: string }[]
    }) => Promise<string>
  }
}

interface InternalProxies {
  auth: AuthServiceMethods
  user: UserServiceMethods
  permission: PermissionServiceMethods
  general: GeneralServiceMethods
  iap: IAPServiceMethods
  data: DataServiceMethods
  additional: AdditionalServiceMethods
  l10n: L10nServiceMethods
}

let proxies: InternalProxies | null = null
let appVersion: string = '0.0.0'

/** @internal — set by initializeNativeBridge. Throws if called twice. */
export function __setProxies(next: InternalProxies): void {
  if (proxies) throw new Error('native services already initialized')
  proxies = next
}

/** @internal — set by initializeNativeBridge. */
export function __setAppVersion(next: string): void {
  appVersion = next
}

/** @internal — test-only reset so each test starts from a clean module state. */
export function __resetForTests(): void {
  proxies = null
  appVersion = '0.0.0'
  pageVisibleOnce.__resetForTests()
}

function requireProxy<K extends keyof InternalProxies>(
  key: K,
): InternalProxies[K] {
  if (!proxies) {
    throw new Error(
      'native services not initialized — call initializeNativeBridge() first',
    )
  }
  return proxies[key]
}

export const native: NativeServices = {
  auth: {
    auth: () => requireProxy('auth').auth(),
    bindLegacyUser: () => requireProxy('auth').bindLegacyUser(),
  },
  user: {
    getUserInfo: () => requireProxy('user').getUserInfo(),
    refreshToken: () => requireProxy('user').refreshToken(),
    refreshServiceLevel: () => requireProxy('user').refreshServiceLevel(),
  },
  permission: {
    getHasNotificationPermission: () =>
      requireProxy('permission').getHasNotificationPermission(),
    requestNotificationPermission: () =>
      requireProxy('permission').requestNotificationPermission(),
  },
  general: {
    close: () => requireProxy('general').close(),
    logEvent: (p) => {
      console.log(
        '[native-services] logEvent',
        p.event_name,
        JSON.stringify(p.event_params ?? {}),
      )
      return requireProxy('general').logEvent(p)
    },
    openURL: (url) => requireProxy('general').openURL(url),
    pageReady: () => requireProxy('general').pageReady(),
    pageAbort: () => requireProxy('general').pageAbort(),
    complete: () => requireProxy('general').complete(),
    getPageContext: () => requireProxy('general').getPageContext(),
    logFatalError: (p) => requireProxy('general').logFatalError(p),
    logNonFatalError: (p) => requireProxy('general').logNonFatalError(p),
  },
  data: {
    fullSync: () => requireProxy('data').fullSync(),
  },
  iap: {
    getPrice: (p) => requireProxy('iap').getPrice(p),
    purchase: (p) => requireProxy('iap').purchase(p),
    closeTransaction: (p) => requireProxy('iap').closeTransaction(p),
    getUnclosedTransaction: (p) =>
      requireProxy('iap').getUnclosedTransaction(p),
    getAllUnclosedTransactions: () =>
      requireProxy('iap').getAllUnclosedTransactions(),
    getAllEntitlements: () => requireProxy('iap').getAllEntitlements(),
    getAvailableSubscriptionProducts: () =>
      requireProxy('iap').getAvailableSubscriptionProducts(),
    getStoreRegion: () => requireProxy('iap').getStoreRegion(),
    getReceiptSystemAccountId: () =>
      requireProxy('iap').getReceiptSystemAccountId(),
  },
  additional: {
    getInitialThemeId: () => requireProxy('additional').getInitialThemeId(),
  },
  l10n: {
    getLocalizedString: async (p) => {
      if (!compare(appVersion, '5.17.0', '>=')) return p.key
      const result = await requireProxy('l10n').getLocalizedString(p)
      if (!result.success) return p.key
      return result.data ?? p.key
    },
  },
}

export { pageVisibleOnce }
